import {AfterContentInit, Component, EventEmitter, OnDestroy, ViewChild} from '@angular/core';
import {IGridData}                                                       from '../../../ap-interface';
import {ApPermissions}                                                   from 'invoker-transport';
import {ApDynComponentComponent}                                         from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {ApDynGridColumnConfigBase}                                       from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridPagerConfigBase}                                        from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {ApDynGridPropertyColumnConfig}                                   from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardSection
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {ApDynGridEditColumnConfig}                                       from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {find}                                                            from 'lodash';
import {AccessPermissionsStore}                                          from '../../../stores/settings/access.permission.store';
import {filter, map}                                                     from 'rxjs/operators';
import {FarmUsersStore}                                                  from '../../../stores/farm/farm.users.store';
import {MapViewStore}                                                    from '../../../stores/layout/mapview.store';
import {TranslationStore}                                                from '../../../stores/translation/translation.store';
import {
  ApDynGridStatusSwitchColumnConfig,
  IStateChange
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-status-switch-column.config';
import {Subscription}                                                    from 'rxjs';
import {UserGroupStore}                                                  from '../../../stores/settings/user.group.store';
import {GetPermissionPipe}                                               from '../../../ap-permission/pipes/get-permission.pipe';
import IGroupMapping = Data.AccessPermission.IGroupMapping;
import IAccessPermissions = Data.AccessPermission.IAccessPermissions;

interface IAccessPermissionsGridData extends IGridData<IAccessPermissions> {
  Module: string;
  Feature: string;
  Admin: number;
  StatusClassAdmin: string;
  StatusDescAdmin: string;
  User: number;
  StatusClassUser: string;
  StatusDescUser: string;
  Consultant: number;
  StatusClassConsultant: string;
  StatusDescConsultant: string;
}

@Component({
  selector: 'ap-access-permissions',
  template: `
    <ap-dyncomponent [caption]="'Global_Access_Permissions'"
                     [canSearch]="true"
                     [headerIcon]="'ap-icon-settings'"
                     [filterTooltip]="'filterTooltip'"
                     [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="accessPermissionsLoading$"
                     [pager]="pager"
                     [groupable]="true"
                     [selectable]="{checkboxOnly: true, mode: 'single'}"
    ></ap-dyncomponent>`
})
export class ApAccessPermissionsComponent implements AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  columns: ApDynGridColumnConfigBase[] = [];
  public accessPermissionsLoading$ = this.permissionsStore.Listen(s => s.loading);
  public items$ = this.permissionsStore.Listen(s => s.data).pipe(
    filter(d => !!d),
    map(d => d.Convert<IAccessPermissionsGridData>(p => {
        const admin = this.getGroupAccessPermissions(p, 'Permission_Group_Administrator');
        const user = this.getGroupAccessPermissions(p, 'Permission_Group_User');
        const consultant = this.getGroupAccessPermissions(p, 'Permission_Group_Consultants');
        return {
          Id: p.Id,
          SourceItem: p,
          Module: this.translationStore.FindTranslationForSelectedLanguage(p.Featuremodul_Translationkey),
          Feature: this.translationStore.FindTranslationForSelectedLanguage(p.Feature_Translationkey),
          Admin: admin ? 1 : -1,
          StatusClassAdmin: admin ? 'ap-status-green' : 'ap-status-red',
          StatusDescAdmin: admin ? 'Global__Active' : 'Global__Inactive',
          User: user ? 1 : -1,
          StatusClassUser: user ? 'ap-status-green' : 'ap-status-red',
          StatusDescUser: user ? 'Global__Active' : 'Global__Inactive',
          Consultant: consultant ? 1 : -1,
          StatusClassConsultant: consultant ? 'ap-status-green' : 'ap-status-red',
          StatusDescConsultant: consultant ? 'Global__Active' : 'Global__Inactive'
        };
      })
    ));
  pager: ApDynGridPagerConfigBase;

  private _subscriptions: Subscription[] = [];

  constructor(private permissionsStore: AccessPermissionsStore,
              private mapViewStore: MapViewStore,
              private farmUsersStore: FarmUsersStore,
              private translationStore: TranslationStore,
              private userGroupStore: UserGroupStore,
              private permissionPipe: GetPermissionPipe) {
    this.mapViewStore.hideMapView();
  }

  ngAfterContentInit(): void {
    this.gridBuilder();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  getGroupAccessPermissions(dataItem: IAccessPermissions, usergroupName: string): boolean {
    const firstGroup: IGroupMapping = find(dataItem.Groups, (i: IGroupMapping) => i.usergroup_name === usergroupName);
    if (!firstGroup) {
      return false;
    }
    return firstGroup.access_permission;
  }

  private gridBuilder(): void {
    this.permissionPipe.transform(ApPermissions.EDIT_ACCESS_RIGHTS).subscribe(permission => {
      this.columns = [
        new ApDynGridPropertyColumnConfig({
          field: 'Module',
          title: 'Global__Module',
          filterable: true,
          headerFilterable: true,
          width: 150,
          sortIndex: 0,
          sortDesc: false,
          groupIndex: 0,
          groupDesc: false
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'Feature',
          title: 'Global_Access_Permission',
          filterable: true,
          headerFilterable: true,
          width: 150,
          sortIndex: 1,
          sortDesc: false,
        }),
        new ApDynGridStatusSwitchColumnConfig({
          field: 'Admin', title: 'Permission_Group_Administrator',
          disableGreen: !permission,
          disableRed: !permission,
          showYellowState: false,
          toolTipGreen: 'Global__Active',
          toolTipRed: 'Global__Inactive',
          stateChange: this._createEventEmitter(
            (event: IStateChange) => {
              switch (event.state) {
                case -1:
                  this._setPermission(event.item['SourceItem'], false, 'Permission_Group_Administrator');
                  break;
                case 1:
                  this._setPermission(event.item['SourceItem'], true, 'Permission_Group_Administrator');
                  break;
              }
            }),
        }),
        new ApDynGridStatusSwitchColumnConfig({
          field: 'User', title: 'Permission_Group_User',
          disableGreen: !permission,
          disableRed: !permission,
          showYellowState: false,
          toolTipGreen: 'Global__Active',
          toolTipRed: 'Global__Inactive',
          stateChange: this._createEventEmitter(
            (event: IStateChange) => {
              switch (event.state) {
                case -1:
                  this._setPermission(event.item['SourceItem'], false, 'Permission_Group_User');
                  break;
                case 1:
                  this._setPermission(event.item['SourceItem'], true, 'Permission_Group_User');
                  break;
              }
            }),
        }),
        new ApDynGridStatusSwitchColumnConfig({
          field: 'Consultant', title: 'Permission_Group_Consultants',
          disableGreen: !permission,
          disableRed: !permission,
          showYellowState: false,
          toolTipGreen: 'Global__Active',
          toolTipRed: 'Global__Inactive',
          stateChange: this._createEventEmitter(
            (event: IStateChange) => {
              switch (event.state) {
                case -1:
                  this._setPermission(event.item['SourceItem'], false, 'Permission_Group_Consultants');
                  break;
                case 1:
                  this._setPermission(event.item['SourceItem'], true, 'Permission_Group_Consultants');
                  break;
              }
            }),
        }),
        new ApDynGridEditColumnConfig(
          {
            disabled: true
          }
        )
      ];
    });
    this.pager = new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardSection()
    );
  }

  private _createEventEmitter<T>(generatorOrNext): EventEmitter<T> {
    const eventEmitter = new EventEmitter<T>();
    this._subscriptions.push(eventEmitter.subscribe(generatorOrNext));
    return eventEmitter;
  }

  private _setPermission(data: IAccessPermissions, value: boolean, group: string): void {
    const userGroup = data.Groups.find(_ => _.usergroup_name === group);
    const groupPer = this.userGroupStore.getUserGroups().find(_ => _.Id === userGroup.usergroup_id);
    if (userGroup === undefined || userGroup === null) {
      return;
    }
    if (value) {
      this.permissionsStore.insertPermission(data.FarmId, groupPer, data.Feature_Id);
    } else {
      this.permissionsStore.deletePermission(data.FarmId, groupPer, data.Feature_Id);
    }
  }
}
